import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';

import { SUPPRESS_GLOBAL_ERROR_TOAST } from '@/common/application/constants/mutationMeta';
import { blocksQueryKey } from '@/blocks/application/hooks/useBlocks';
import { blockUsagesQueryKey } from '@/blocks/application/hooks/useBlockUsages';
import {
  isPublishedStatus,
  openDraftIdOf,
  publishSavedBlockDraft,
  saveBlockDraftVersion,
  saveBlockHeader,
  type SaveBlockDraftArgs,
  type SavedBlockVersion
} from '@/blocks/application/hooks/blockDraftRequests';

export const NOTHING_TO_PUBLISH_MESSAGE = 'There is nothing to publish: no edits and no open draft.';

export const blockDetailQueryKey = (blockId: string) => ['document-builder', 'block', blockId] as const;

/**
 * Refetches everything a block write can change: the library list, the block's detail (its
 * `draftVersionId` and versions) and its usages. Shared by Save and Publish so both read the server
 * the same way after a write.
 */
export async function refreshBlockQueries(queryClient: QueryClient, blockId: string): Promise<void> {
  await queryClient.invalidateQueries({ queryKey: blocksQueryKey, refetchType: 'all' });
  await queryClient.invalidateQueries({ queryKey: blockDetailQueryKey(blockId) });
  await queryClient.invalidateQueries({ queryKey: blockUsagesQueryKey(blockId) });
  await queryClient.refetchQueries({ queryKey: blockDetailQueryKey(blockId) });
}

/** What a Publish settled: the version that is now live. Promoting a clean open draft writes no body, so only the id and status are known then. */
export type PublishedBlockVersion = Pick<SavedBlockVersion, 'blockVersionId' | 'status'> & Partial<SavedBlockVersion>;

/**
 * Publish saves the open draft first (PUT when `draftVersionId` is set, otherwise POST
 * `asDraft: true`), then promotes it with `/publish`. A PDF Templates API older than PDF-1 ignores
 * `asDraft` and answers the POST with a version that is already `Published`; `/publish` on that id
 * is a 409 ("Only draft block versions can be published"), so the promote is skipped when the save
 * already came back Published.
 *
 * A clean open draft (`writeVersion: false`) is promoted as it is: no header PUT unless the name
 * changed, no draft PUT, only `POST …/versions/{draftVersionId}/publish` (BE-01). With neither an
 * edit nor an open draft there is nothing to publish: nothing is written and the mutation rejects.
 */
export const usePublishBlockVersion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    // Both callers (the editor's Publish, New block's Publish) say what went wrong themselves; without
    // this the CRM host toasts a generic "Operation failed" on top.
    meta: SUPPRESS_GLOBAL_ERROR_TOAST,
    mutationFn: async (args: SaveBlockDraftArgs): Promise<PublishedBlockVersion> => {
      if (args.writeVersion === false) {
        const openDraftId = openDraftIdOf(args.draftVersionId);
        if (!openDraftId) throw new Error(NOTHING_TO_PUBLISH_MESSAGE);

        await saveBlockHeader(args);
        await publishSavedBlockDraft(args.blockId, Number(openDraftId));
        return { blockVersionId: Number(openDraftId), status: 'Published' };
      }

      await saveBlockHeader(args);
      const saved = await saveBlockDraftVersion(args);
      if (isPublishedStatus(saved.status)) {
        return saved;
      }

      await publishSavedBlockDraft(args.blockId, saved.blockVersionId);
      return { ...saved, status: 'Published' };
    },
    onSuccess: (_data, variables) => refreshBlockQueries(queryClient, variables.blockId),
    // A failed `/publish` can follow a draft POST that already landed: refetch so the next Save or
    // Publish writes into that draft (PUT) instead of POSTing another one and getting a 409 (LLAMS-686).
    onError: (_error, variables) => refreshBlockQueries(queryClient, variables.blockId)
  });
};
